import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { update } from "../../../../../api/onboarding";
import { GeaoContext } from "../../../GeaoContextProvider";
import { ClientConfigurationContext } from "../../../../../context/ClientConfigurationContextProvider";
import { PageStatus } from "../../../hooks/useInterviewUpdater";
import { ApiRequestStatus } from "../../../constants/ApiRequestStatus";
import { GeaoPaths } from "../../../constants/GeaoPaths";
import { formDataToQuestionAnswers } from "./mappers";
import { FlowType } from "../../../constants/FlowType";

/**
 * Custom React hook for handling form events in Background Information forms.
 * Supports both Principal and Producer flows with appropriate navigation paths.
 * Manages form submission, save and exit functionality, and API request states.
 * 
 * @param {Object} options - Configuration options for the hook
 * @param {Function} [options.getValues=() => ({})] - Function to retrieve current form values
 * @param {Function} [options.trigger=() => Promise.resolve()] - Function to trigger form validation
 * @param {string} options.flowType - The flow type ('principal' or 'producer')
 * @param {string} options.componentName - The component name for activity tracking
 * @returns {Object} Object containing form event handlers and loading state
 * @returns {Function} returns.onSubmit - Handler for form submission that navigates to next page
 * @returns {Function} returns.onSaveAndExit - Handler for saving progress and returning to checklist
 * @returns {boolean} returns.saving - Boolean indicating if an API request is in progress
 */
export default function useFormEvents({
    getValues = () => ({}),
    trigger = () => Promise.resolve(true),
    flowType,
    componentName
}) {
    const navigate = useNavigate();

    const {
        agentInterview,
        setAgentInterview,
        interviewIsCompleted
    } = useContext(GeaoContext);

    const clientConfiguration = useContext(ClientConfigurationContext);

    const [updateStatus, setUpdateStatus] = useState(ApiRequestStatus.NotSent);
    const [nextPath, setNextPath] = useState(GeaoPaths.CreateYourProfileChecklist);

    const saving = updateStatus === ApiRequestStatus.Pending;

    // Validate required parameters
    if (!flowType || !componentName) {
        throw new Error('flowType and componentName are required parameters for useFormEvents');
    }

    if (!Object.values(FlowType).includes(flowType)) {
        throw new Error(`Invalid flowType: ${flowType}. Must be one of: ${Object.values(FlowType).join(', ')}`);
    }

    /**
     * Get the next path based on flow type and offshore settings.
     * For Producer flow with offshore enabled and user is offshore resident, navigate to Submit Application.
     * Otherwise, navigate to Review and Sign or appropriate path based on flow type.
     */
    const getNextPath = () => {
        // Check for offshore CSR in Producer flow
        if (flowType === FlowType.Producer) {
            const enableOffshore = clientConfiguration?.enableOffshore === true;
            const isOffshoreResident = agentInterview?.questionAnswers?.OffshoreResident?.answer === "true";

            if (enableOffshore && isOffshoreResident) {
                return GeaoPaths.ProducerSubmitApplication;
            }
            return GeaoPaths.ProducerReviewAndSign;
        }

        switch (flowType) {
            case FlowType.Principal:
                return GeaoPaths.PrincipalAgencyOfficeInformation;
            default:
                return GeaoPaths.CreateYourProfileChecklist;
        }
    };

    /**
     * Effect hook that handles navigation after successful API updates.
     * Navigates to the specified next path after successful update, including assessment-based navigation.
     */
    useEffect(() => {
        if (updateStatus === ApiRequestStatus.Success) {
            // Handle assessment-based navigation first
            if (agentInterview?.assessments?.NotACaptiveAgent?.status === "Rejected") {
                navigate(GeaoPaths.CreateYourProfileKickout);
                return;
            }

            // Producer-specific assessment checks
            if (flowType === FlowType.Producer) {
                if (agentInterview?.assessments?.ParentAgencyHasOnboarded?.status === "Rejected" ||
                    agentInterview?.assessments?.ParentAgencyHasOnboarded?.status === "InProgress") {
                    navigate(GeaoPaths.ProducerKickout);
                    return;
                }
            }

            // Default navigation to next path
            navigate(nextPath);
        }
    }, [updateStatus, nextPath, navigate, agentInterview, flowType]);

    /**
     * Handles form submission and proceeds to the next step in the flow.
     * Sets the appropriate next path based on flow type and updates the interview.
     * 
     * @param {Object} formData - The form data to be submitted
     */
    const onSubmit = (formData) => {
        setNextPath(getNextPath());
        updateInterview(formData, false);
    };

    /**
     * Handles the save and exit action.
     * Triggers form validation, then saves current form values and navigates back to checklist.
     */
    const onSaveAndExit = () => {
        trigger().then(validationPassed => {
            if (validationPassed) {
                setNextPath(GeaoPaths.CreateYourProfileChecklist);
                updateInterview(getValues(), true);
            }
        });
    };

    /**
     * Updates the agent interview with form data via API call.
     * Manages API request state and handles success/error scenarios.
     * Includes assessment-specific navigation logic for both Principal and Producer flows.
     * 
     * @param {Object} formData - The form data to be saved
     * @param {boolean} isSaveAndExit - Whether this is a save and exit operation
     */
    const updateInterview = (formData, isSaveAndExit) => {
        if (updateStatus === ApiRequestStatus.Pending) return;

        // Create activity request for save and exit operations
        const activityRequest = isSaveAndExit ? {
            ActivityCategory: "GEAO_ONBOARDING",
            ActivitySubType: "SAVE_AND_EXIT",
            SourceApplication: "AGIWUI",
            AdditionalData: {
                ComponentName: componentName
            }
        } : null;

        const questionAnswers = Object.fromEntries(
            Object.entries({
                ...agentInterview?.questionAnswers,
                ...formDataToQuestionAnswers(formData)
            }).filter(([, value]) => value !== null)
        );


        const updatedInterview = {
            identityType: "Agent",
            interview: {
                ...agentInterview,
                questionAnswers: questionAnswers,
                pageStatus: {
                    ...agentInterview.pageStatus,
                    [componentName]: PageStatus.Completed
                }
            },
            activityRequest: activityRequest
        };

        setUpdateStatus(ApiRequestStatus.Pending);

        update(updatedInterview,
            interview => {
                setAgentInterview(interview);
                setUpdateStatus(ApiRequestStatus.Success);
            },
            () => {
                // Finally callback
            },
            () => {
                // Error callback
                setUpdateStatus(ApiRequestStatus.Error);
            }
        );
    };

    return {
        onSubmit,
        onSaveAndExit,
        saving: saving || interviewIsCompleted
    };
}